import React, { useContext, useState } from 'react';
import Page from '../..';
import Table, { TableHeader } from '../../../Table';
import Form from '../../../Form';
import { ValidityContext } from '../../../../provider/ValidityProvider';
import SVG from '../../../SVG';
import { getFilterColumn, setFilterColumn } from '../../../../store/utils';
import Led from '../../../Led';

const PageValidityValidities = () => {
  const {
    validities,
    Can,
    ability,
    editValidity,
    setModal,
    onLineClickHandler,
    allValidityChecks,
    validityCheckAll,
    checkValidityIds,
    checkValidityId,
    TableControl,
    setValidity,
    deleteValidityLoadings,
    getSortField,
    setSortField,
    autocompleteValidities,
    getValidity,
    searchFilter,
    setSearchFilter,
    loadingValidities,
    priorityItems,
  } = useContext(ValidityContext)!.controllers.validity;

  const canDeleteValidity = ability?.can('delete', 'Validity');
  const canUpdateValidity = ability?.can('update', 'Validity');
  const canUpdateOrDeleteValidity = canDeleteValidity || canUpdateValidity;
  const canUpdateAndDeleteValidity = canDeleteValidity && canUpdateValidity;
  const autocompletes = getValidity('autocompletes.validity');

  const [codeLoading, setCodeLoading] = useState(false);
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);

  const autocompleteCode = (e) => {
    setCodeLoading(true);
    autocompleteValidities('subProductId', e.target.value)
      .then((data) => {
        setValidity(`autocompletes.validity.code.data`, data);
      })
      .finally(() => {
        setCodeLoading(false);
      });
  };

  const autocompleteBarcode = (e) => {
    setBarcodeLoading(true);
    autocompleteValidities('barcodeNumber', e.target.value)
      .then((data) => {
        setValidity(`autocompletes.validity.barcode.data`, data);
      })
      .finally(() => {
        setBarcodeLoading(false);
      });
  };

  const autocompleteProduct = (e) => {
    setProductLoading(true);
    autocompleteValidities('productShortDescription', e.target.value)
      .then((data) => {
        setValidity(`autocompletes.validity.product.data`, data);
      })
      .finally(() => {
        setProductLoading(false);
      });
  };

  const autocompleteSection = (e) => {
    setSectionLoading(true);
    autocompleteValidities('sectionDescription', e.target.value, 'sectionId')
      .then((data) => {
        setValidity(`autocompletes.validity.section.data`, data);
      })
      .finally(() => {
        setSectionLoading(false);
      });
  };

  const autocompleteStatus = (e) => {
    setStatusLoading(true);
    autocompleteValidities('statusName', e.target.value)
      .then((data) => {
        setValidity(`autocompletes.validity.statusName.data`, data);
      })
      .finally(() => {
        setStatusLoading(false);
      });
  };

  const autocompleteBranch = (e) => {
    setBranchLoading(true);
    autocompleteValidities('branchName', e.target.value, 'branchCode')
      .then((data) => {
        setValidity(`autocompletes.validity.branch.data`, data);
      })
      .finally(() => {
        setBranchLoading(false);
      });
  };

  const productItems = autocompletes.product.data?.map((item) => ({
    id: item.id,
    name: `${item.value}`,
    data: item,
  }));

  const codeItems = autocompletes.code.data?.map((item) => ({
    id: item.id,
    name: `${item.value}`,
    data: item,
  }));

  const barcodeItems = autocompletes.barcode.data?.map((item) => ({
    id: item.id,
    name: `${item.value}`,
    data: item,
  }));

  const sectionItems = autocompletes.section.data?.map((item) => ({
    id: item.id,
    name: `${item.id} - ${item.value}`,
    data: item,
  }));

  const statusItems = autocompletes.statusName.data?.map((item) => ({
    id: item.id,
    name: `${item.value}`,
    data: item,
  }));

  const branchItems = autocompletes.branch.data?.map((item) => ({
    id: item.id,
    name: `${item.id} - ${item.value}`,
    data: item,
  }));

  const codeValue = getFilterColumn('subProductId', searchFilter, 'LIKE');

  const barcodeValue = getFilterColumn('barcodeNumber', searchFilter, 'LIKE');

  const productShortDescriptionValue = getFilterColumn(
    'productShortDescription',
    searchFilter,
    'LIKE',
  );

  const sectionDescriptionValue = getFilterColumn(
    'sectionDescription',
    searchFilter,
    'LIKE',
  );

  const statusValue = getFilterColumn('statusName', searchFilter, 'LIKE');

  const branchNameValue = getFilterColumn('branchName', searchFilter, 'LIKE');

  const priorityFilter = getFilterColumn('priority', searchFilter);
  const priorityValue =
    priorityItems.find(({ data }) => data === priorityFilter)?.name ?? '';

  const isHighPriority = priorityFilter === 'high';
  const isMediumPriority = priorityFilter === 'medium';
  const isLowPriority = priorityFilter === 'low';
  const isDefeated = priorityFilter === 'defeated';
  const emptyPriority = !priorityFilter;

  const getPriority = (priority: ValidityPriority | null) => {
    if (priority === 'high') {
      return <Led color="red" animation />;
    } else if (priority === 'medium') {
      return <Led color="yellow" animation />;
    } else if (priority === 'low') {
      return <Led color="green" />;
    } else {
      return <Led color="blue" />;
    }
  };

  const headers: TableHeader[] = [
    {
      text: (
        <Form.Input.Checkbox
          checked={allValidityChecks}
          onChange={validityCheckAll}
        />
      ),
      value: 'check',
      width: 'auto',
      isFixed: true,
      tooltip: {
        translateX: 0.25,
      },
    },
    {
      text: 'Código',
      value: 'subProductId',
      width: '120px',
      sort: {
        value: getSortField('subProductId.keyword'),
        onChange: setSortField('subProductId.keyword'),
      },
      filter: {
        label: 'Código',
        value: codeValue,
        items: codeItems,
        onChange: (e) => {
          const value = e.target.value;
          let filter = setFilterColumn(
            'subProductId',
            value,
            searchFilter,
            'LIKE',
            'AND',
          );
          setSearchFilter(filter);
        },
        onSelected: ({ data }) => {
          let filter = setFilterColumn(
            'subProductId',
            data.value,
            searchFilter,
            'LIKE',
            'AND',
          );
          setSearchFilter({ ...filter, page: 1 });
        },
        handleItems: autocompleteCode,
        loading: codeLoading,
      },
      tooltip: {
        translateX: 0.25,
      },
      onClear: () => {
        let filter = setFilterColumn('subProductId', '', searchFilter);
        setSearchFilter(filter);
      },
    },
    {
      text: 'Produto',
      value: 'productShortDescription',
      width: '0.8fr',
      sort: {
        value: getSortField('productShortDescription.keyword'),
        onChange: setSortField('productShortDescription.keyword'),
      },
      filter: {
        label: 'Produto',
        value: productShortDescriptionValue,
        items: productItems,
        onChange: (e) => {
          const value = e.target.value;
          let filter = setFilterColumn(
            'productShortDescription',
            value,
            searchFilter,
            'LIKE',
            'AND',
          );
          setSearchFilter(filter);
        },
        onSelected: ({ data }) => {
          let filter = setFilterColumn(
            'productShortDescription',
            data.value,
            searchFilter,
            'LIKE',
            'AND',
          );
          setSearchFilter({ ...filter, page: 1 });
        },
        handleItems: autocompleteProduct,
        loading: productLoading,
      },
      tooltip: {
        translateX: 0.25,
      },
      onClear: () => {
        let filter = setFilterColumn(
          'productShortDescription',
          '',
          searchFilter,
        );
        setSearchFilter(filter);
      },
    },
    {
      text: 'Código de barras',
      value: 'barcodeNumber',
      width: '0.5fr',
      sort: {
        value: getSortField('barcodeNumber.keyword'),
        onChange: setSortField('barcodeNumber.keyword'),
      },
      tooltip: {
        translateX: 0.5,
      },
      filter: {
        label: 'Código de barras',
        value: barcodeValue,
        items: barcodeItems,
        onChange: (e) => {
          const value = e.target.value;
          let filter = setFilterColumn(
            'barcodeNumber',
            value,
            searchFilter,
            'LIKE',
            'AND',
          );
          setSearchFilter(filter);
        },
        onSelected: ({ data }) => {
          let filter = setFilterColumn(
            'barcodeNumber',
            data.value,
            searchFilter,
            'LIKE',
            'AND',
          );
          setSearchFilter({ ...filter, page: 1 });
        },
        handleItems: autocompleteBarcode,
        loading: barcodeLoading,
      },
      onClear: () => {
        let filter = setFilterColumn('barcodeNumber', '', searchFilter);
        setSearchFilter(filter);
      },
    },

    {
      text: 'Quantidade',
      value: 'quantity',
      width: '0.4fr',
      sort: {
        value: getSortField('quantity'),
        onChange: setSortField('quantity'),
      },
      style: {
        fontWeight: 'bold',
      },
    },
    {
      text: 'Status',
      value: 'statusName',
      width: '110px',
      color: () => 'white',
      background: ({ statusColor }) => statusColor,
      // sort: {
      //   value: getSortField('status.keyword'),
      //   onChange: setSortField('status.keyword'),
      // },
      tooltip: {
        translateX: 0.5,
      },
      filter: {
        label: 'Status',
        value: statusValue,
        items: statusItems,
        onChange: (e) => {
          const value = e.target.value;
          let filter = setFilterColumn(
            'statusName',
            value,
            searchFilter,
            'LIKE',
            'AND',
          );
          setSearchFilter(filter);
        },
        onSelected: ({ data }) => {
          let filter = setFilterColumn(
            'statusName',
            data.value,
            searchFilter,
            'LIKE',
            'AND',
          );
          setSearchFilter({ ...filter, page: 1 });
        },
        handleItems: autocompleteStatus,
        loading: statusLoading,
      },
      onClear: () => {
        let filter = setFilterColumn('statusName', '', searchFilter);
        setSearchFilter(filter);
      },
      style: {
        justifyContent: 'center',
        borderRadius: '3px',
        fontWeight: 'bold',
      },
    },
    {
      text: 'Prioridade',
      value: '',
      width: '115px',
      mask: ({ priority }) => (
        <div
          style={{
            margin: 'auto',
          }}
        >
          {getPriority(priority)}
        </div>
      ),
      tooltip: {
        translateX: 0.5,
      },
      filter: {
        label: 'Prioridade',
        value: priorityValue,
        items: priorityItems,
        onChange: () => {},
        onSelected: ({ data }) => {
          let filter = setFilterColumn(
            'priority',
            data,
            searchFilter,
            'LIKE',
            'AND',
          );
          setSearchFilter({ ...filter, page: 1 });
        },
        handleItems: autocompleteStatus,
      },
      onClear: () => {
        let filter = setFilterColumn('priority', '', searchFilter);
        setSearchFilter(filter);
      },
    },
    {
      text: 'Seção',
      value: 'sectionDescription',
      width: '0.5fr',
      sort: {
        value: getSortField('sectionDescription.keyword'),
        onChange: setSortField('sectionDescription.keyword'),
      },
      filter: {
        label: 'Seção',
        value: sectionDescriptionValue,
        items: sectionItems,
        onChange: (e) => {
          const value = e.target.value;
          let filter = setFilterColumn(
            'sectionDescription',
            value,
            searchFilter,
            'LIKE',
            'OR',
          );
          filter = setFilterColumn('sectionId', value, filter, 'LIKE', 'OR');
          setSearchFilter(filter);
        },
        onSelected: ({ data }) => {
          let filter = setFilterColumn(
            'sectionDescription',
            data.value,
            searchFilter,
            'LIKE',
            'AND',
          );
          filter = setFilterColumn('sectionId', data.id, filter, 'LIKE', 'AND');
          setSearchFilter({ ...filter, page: 1 });
        },
        handleItems: autocompleteSection,
        loading: sectionLoading,
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        let filter = setFilterColumn('sectionDescription', '', searchFilter);
        filter = setFilterColumn('sectionId', '', filter);
        setSearchFilter(filter);
      },
    },
    {
      text: 'Filial',
      value: 'branchName',
      width: '0.6fr',
      sort: {
        value: getSortField('branchName.keyword'),
        onChange: setSortField('branchName.keyword'),
      },
      filter: {
        label: 'Filial',
        value: branchNameValue,
        items: branchItems,
        onChange: (e) => {
          const value = e.target.value;
          let filter = setFilterColumn(
            'branchName',
            value,
            searchFilter,
            'LIKE',
            'OR',
          );
          filter = setFilterColumn('branchCode', value, filter, 'LIKE', 'OR');
          setSearchFilter(filter);
        },
        onSelected: ({ data }) => {
          let filter = setFilterColumn(
            'branchName',
            data.value,
            searchFilter,
            'LIKE',
            'AND',
          );
          filter = setFilterColumn(
            'branchCode',
            data.id,
            filter,
            'LIKE',
            'AND',
          );
          setSearchFilter({ ...filter, page: 1 });
        },
        handleItems: autocompleteBranch,
        loading: branchLoading,
      },
      tooltip: {
        translateX: 0.5,
      },
      onClear: () => {
        let filter = setFilterColumn('branchName', '', searchFilter);
        filter = setFilterColumn('branchCode', '', filter);
        setSearchFilter(filter);
      },
    },
    {
      text: 'Validade',
      value: 'validity',
      width: '130px',
      mask: ({ validity }) =>
        new Date(validity).toLocaleString('pt-BR').split(',')[0],
      sort: {
        value: getSortField('validity'),
        onChange: setSortField('validity'),
      },
      interval: [
        {
          value: getFilterColumn('validity', searchFilter, 'GTE'),
          onChange: (e) => {
            const initialDate = e.target.value;
            let filter = setFilterColumn(
              'validity',
              initialDate,
              searchFilter,
              'GTE',
              'AND',
              'dd/MM/yyyy',
            );
            setSearchFilter(filter);
          },
          label: 'Data inicial',
        },
        {
          value: getFilterColumn('validity', searchFilter, 'LTE'),
          onChange: (e) => {
            const finalDate = e.target.value;
            let filter = setFilterColumn(
              'validity',
              finalDate,
              searchFilter,
              'LTE',
              'AND',
              'dd/MM/yyyy',
            );
            setSearchFilter(filter);
          },
          label: 'Data final',
        },
      ],
      tooltip: {
        translateX: 0.75,
      },
      onClear: () => {
        let filter = setFilterColumn('validity', '', searchFilter, 'GTE');
        filter = setFilterColumn('validity', '', filter, 'LTE');
        setSearchFilter(filter);
      },
    },
    {
      text: 'Criado em',
      value: 'createdAt',
      width: '130px',
      mask: ({ createdAt }) => new Date(createdAt).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('createdAt'),
        onChange: setSortField('createdAt'),
      },
      tooltip: {
        translateX: 0.75,
      },
      interval: [
        {
          value: getFilterColumn('createdAt', searchFilter, 'GTE'),
          onChange: (e) => {
            const initialDate = e.target.value;
            let filter = setFilterColumn(
              'createdAt',
              initialDate,
              searchFilter,
              'GTE',
              'AND',
              'dd/MM/yyyy',
            );
            setSearchFilter(filter);
          },
          label: 'Data inicial',
        },
        {
          value: getFilterColumn('createdAt', searchFilter, 'LTE'),
          onChange: (e) => {
            const finalDate = e.target.value;
            let filter = setFilterColumn(
              'createdAt',
              finalDate,
              searchFilter,
              'LTE',
              'AND',
              'dd/MM/yyyy',
            );
            setSearchFilter(filter);
          },
          label: 'Data final',
        },
      ],
    },
    {
      text: 'Atualizado em',
      value: 'updatedAt',
      width: '130px',
      mask: ({ updatedAt }) => new Date(updatedAt).toLocaleString('pt-BR'),
      sort: {
        value: getSortField('updatedAt'),
        onChange: setSortField('updatedAt'),
      },
      tooltip: {
        translateX: 0.9,
      },
      interval: [
        {
          value: getFilterColumn('updatedAt', searchFilter, 'GTE'),
          onChange: (e) => {
            const initialDate = e.target.value;
            let filter = setFilterColumn(
              'updatedAt',
              initialDate,
              searchFilter,
              'GTE',
              'AND',
              'dd/MM/yyyy',
            );
            setSearchFilter(filter);
          },
          label: 'Data inicial',
        },
        {
          value: getFilterColumn('updatedAt', searchFilter, 'LTE'),
          onChange: (e) => {
            const finalDate = e.target.value;
            let filter = setFilterColumn(
              'updatedAt',
              finalDate,
              searchFilter,
              'LTE',
              'AND',
              'dd/MM/yyyy',
            );
            setSearchFilter(filter);
          },
          label: 'Data final',
        },
      ],
    },
    ...(() =>
      canUpdateOrDeleteValidity
        ? [
            {
              text: '',
              value: 'control',
              width: canUpdateAndDeleteValidity ? '80px' : '40px',
              isFixed: true,
              fixedPosition: 'right' as any,
            },
          ]
        : [])(),
  ];

  const items = validities.map((validity, index) => ({
    $isSelected: checkValidityIds.includes(validity.id),
    check: () => (
      <Form.Input.Checkbox
        checked={checkValidityIds.includes(validity.id)}
        onChange={() => checkValidityId(validity.id)}
      />
    ),
    ...validity,
    ...(() =>
      canUpdateOrDeleteValidity
        ? {
            control: () => (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '10px',
                }}
              >
                <Can I="update" a="Validity">
                  {() => (
                    <label
                      style={{
                        display: 'flex',
                        cursor: 'pointer',
                        alignItems: 'center',
                      }}
                    >
                      {true ? (
                        <SVG.Icon.Edit
                          onClick={() => {
                            editValidity(validity);
                          }}
                          width={25}
                          height={25}
                        />
                      ) : (
                        <SVG.Icon.Loader width={25} height={25} />
                      )}
                    </label>
                  )}
                </Can>
                <Can I="delete" a="Validity">
                  {() => (
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      {!deleteValidityLoadings[index] ? (
                        <SVG.Icon.Trash
                          style={{ padding: '0 2.5px' }}
                          onClick={() => {
                            setModal('single.deleteValidity', true);
                            setValidity('selected.validity', validity.id);
                          }}
                          width={20}
                          height={20}
                        />
                      ) : (
                        <SVG.Icon.Loader width={25} height={25} />
                      )}
                    </label>
                  )}
                </Can>
              </label>
            ),
          }
        : {})(),
  }));

  return (
    <Page className="page-banner">
      <div
        style={{
          position: 'relative',
          transition: 'min-width 500ms, max-width 500ms',
          width: '100%',
          minHeight: 'calc(100vh - 130px)',
          maxHeight: 'calc(100vh - 130px)',
          margin: '0 1rem',
          background: '#DDDDDD',
          borderRadius: '5px',
          overflow: 'hidden',
        }}
      >
        <Table
          headers={headers}
          items={items}
          size="small"
          onLineClick={onLineClickHandler}
          containerStyle={{ height: 'calc(100vh - 210px)' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                columnGap: '1.5rem',
                marginLeft: '1rem',
                color: '#364856',
                marginTop: '1rem',
              }}
            >
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                Prioridade:
              </div>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '0.5rem',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (loadingValidities) return;
                  let filter = setFilterColumn(
                    'priority',
                    isHighPriority ? '' : 'high',
                    searchFilter,
                  );
                  setSearchFilter(filter);
                }}
              >
                <Led color="red" off={!isHighPriority && !emptyPriority} />

                <label style={{ fontWeight: 'bold', cursor: 'pointer' }}>
                  Alta
                </label>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '0.5rem',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (loadingValidities) return;
                  let filter = setFilterColumn(
                    'priority',
                    isMediumPriority ? '' : 'medium',
                    searchFilter,
                  );
                  setSearchFilter(filter);
                }}
              >
                <Led color="yellow" off={!isMediumPriority && !emptyPriority} />
                <label style={{ fontWeight: 'bold', cursor: 'pointer' }}>
                  Média
                </label>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '0.5rem',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (loadingValidities) return;
                  let filter = setFilterColumn(
                    'priority',
                    isLowPriority ? '' : 'low',
                    searchFilter,
                  );
                  setSearchFilter(filter);
                }}
              >
                <Led color="green" off={!isLowPriority && !emptyPriority} />
                <label style={{ fontWeight: 'bold', cursor: 'pointer' }}>
                  Baixa
                </label>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '0.5rem',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (loadingValidities) return;
                  let filter = setFilterColumn(
                    'priority',
                    isDefeated ? '' : 'defeated',
                    searchFilter,
                  );
                  setSearchFilter(filter);
                }}
              >
                <Led color="blue" off={!isDefeated && !emptyPriority} />
                <label style={{ fontWeight: 'bold', cursor: 'pointer' }}>
                  Vencido
                </label>
              </label>
              {loadingValidities ? (
                <SVG.Icon.Loader width={20} height={20} />
              ) : null}
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <TableControl />
            </div>
          </div>
        </Table>
      </div>
    </Page>
  );
};

export default PageValidityValidities;
